import {
  Box,
  IconButton,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Collapse,
  ButtonBase,
  ListItemIcon,
  ClickAwayListener,
  Typography,
} from "@mui/material";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { cloneElement, useEffect, useRef, useState } from "react";
import { tokens } from "../theme";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import PrintIcon from "@mui/icons-material/Print";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MailIcon from "@mui/icons-material/Mail";
import { useNavigate, useLocation } from "react-router-dom";
import ButtonComponent from "../components/ButtonComponent";
import { useDispatch, useSelector } from "react-redux";
import {
  downloadExportedFile,
  exportRestaurantData,
  getDatabasesError,
  getDatabasesStatus,
  getDatabasesSuccess,
  importRestaurantData,
  updateStatus,
} from "../features/backupSlice";
import { toast } from "react-toastify";
import { unwrapResult } from "@reduxjs/toolkit";
import { useRestaurant } from "../hooks/RestaurantContext";
import Error from "../components/Error";
import CustomDialog from "../components/cutomPopUp";
import { UserRole } from "../models/user.model";
import { useAuth } from "../hooks/authHook";
import { useTranslation } from "react-i18next";
const SettingsMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { restaurantId } = useRestaurant();
  const [isParameterListOpen, setIsParameterListOpen] = useState(false);
  const [openBackupRestore, setOpenBackupRestore] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const error = useSelector(getDatabasesError);
  const success = useSelector(getDatabasesSuccess);
  const status = useSelector(getDatabasesStatus);
  const { role } = useAuth();

  const hasAccess = [UserRole.admin].includes(role);

  const toggleParameterList = () => {
    setIsParameterListOpen((prev) => !prev);
  };

  useEffect(() => {
    if (status === "addSuccess") {
      toast.success(success);
      setOpenBackupRestore(false);
      dispatch(updateStatus());
    }
  }, [success, dispatch, status]);

  const handleExport = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    try {
      toast.info(t("settings.exportStarting"), { toastId: "export-starting" });
      const exportAction = await dispatch(exportRestaurantData(restaurantId));
      const exportResult = unwrapResult(exportAction);

      if (!exportResult.success || !exportResult.files) {
        throw new Error(
          exportResult.message || t("settings.exportFailed")
        );
      }

      toast.success(t("settings.exportReady"), { toastId: "export-ready" });

      for (const file of exportResult.files) {
        const { filename } = file;
        const downloadAction = await dispatch(
          downloadExportedFile({ filename, restaurantId })
        );
        const { data: blob, filename: downloadedFilename } =
          unwrapResult(downloadAction);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", downloadedFilename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      toast.success(
        t("settings.exportComplete", { count: exportResult.files.length }),
        { toastId: "export-complete" }
      );
    } catch (err) {
      const errorMessage =
        err.message || t("settings.exportError");
      console.error("Export process failed:", err);
      toast.error(errorMessage);
    } finally {
      setExportLoading(false);
    }
  };

  const handleImport = async (e) => {
    setImportLoading(true);
    const file = e.target.files[0];
    if (!file) {
      setImportLoading(false);
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    await dispatch(importRestaurantData({ body: formData, restaurantId }));
    setImportLoading(false);
  };

  useEffect(() => {
    if (status === "fetchError" || status === "addError") {
      toast.error(error);
    }
  }, [error, status]);

  return (
    <>
      <ClickAwayListener onClickAway={() => setIsParameterListOpen(false)}>
        <Box position="relative" zIndex={4}>
          <IconButton onClick={toggleParameterList}>
            <SettingsIcon />
          </IconButton>
          <Collapse
            in={isParameterListOpen}
            timeout="auto"
            unmountOnExit
            sx={{
              position: "absolute",
              insetInlineEnd: 0,
              top: "40px",
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "10px",
              boxShadow: "none",
            }}
          >
            <List>
              <MenuButtonItem
                onClick={() => {
                  navigate("/settings");
                  toggleParameterList();
                }}
                icon={<TuneIcon />}
                label={t("settings.parameters")}
                colors={colors}
                isActive={location.pathname.startsWith("/settings")}
              />
              {hasAccess && (
                <>
                  <MenuButtonItem
                    onClick={() => {
                      navigate("/currency");
                      toggleParameterList();
                    }}
                    icon={<AttachMoneyIcon />}
                    label={t("settings.currency")}
                    colors={colors}
                    isActive={location.pathname.startsWith("/currency")}
                  />
                  <MenuButtonItem
                    onClick={() => {
                      navigate("/printer");
                      toggleParameterList();
                    }}
                    icon={<PrintIcon />}
                    label={t("settings.printer")}
                    colors={colors}
                    isActive={location.pathname.startsWith("/printer")}
                  />
                  <MenuButtonItem
                    onClick={() => {
                      navigate("/mailing");
                      toggleParameterList();
                    }}
                    icon={<MailIcon />}
                    label={t("settings.email")}
                    colors={colors}
                    isActive={location.pathname.startsWith("/mailing")}
                  />
                  <MenuButtonItem
                    onClick={() => {
                      setOpenBackupRestore(true);
                      toggleParameterList();
                    }}
                    icon={<SettingsBackupRestoreIcon />}
                    label={t("settings.backup")}
                    colors={colors}
                  />
                </>
              )}
            </List>
          </Collapse>
        </Box>
      </ClickAwayListener>
      <CustomDialog
        open={openBackupRestore}
        handleClose={() => setOpenBackupRestore(false)}
        title={t("settings.backupTitle")}
        message={t("settings.backupMessage")}
        cancelText={t("common.cancel")}
        noConfirm={false}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "center",
            mt: 1,
          }}
        >
          <Box
            sx={{
              flex: "1 1 220px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1,
              p: 2,
              borderRadius: "10px",
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: colors.primary[400],
            }}
          >
            <CloudDownloadIcon sx={{ fontSize: 36, color: colors.greenAccent[500] }} />
            <Typography variant="body2" sx={{ color: colors.grey[100] }}>
              {t("settings.exportDescription")}
            </Typography>
            <ButtonComponent
              text={t("settings.export")}
              color="primary"
              onClick={handleExport}
              loading={exportLoading}
            />
          </Box>
          <Box
            sx={{
              flex: "1 1 220px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1,
              p: 2,
              borderRadius: "10px",
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: colors.primary[400],
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 36, color: theme.palette.warning.main }} />
            <Typography variant="body2" sx={{ color: colors.grey[100] }}>
              {t("settings.importDescription")}
            </Typography>
            <input
              type="file"
              accept="application/json"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleImport}
            />
            <ButtonComponent
              text={t("settings.import")}
              color="secondary"
              onClick={() => fileInputRef.current.click()}
              loading={importLoading}
            />
          </Box>
        </Box>
      </CustomDialog>
    </>
  );
};

export const MenuButtonItem = ({ onClick, icon, label, colors, isActive = false }) => {
  const coloredIcon = icon
    ? cloneElement(icon, {
        sx: {
          ...icon.props.sx,
          color: isActive ? colors.redAccent[500] : colors.grey[100],
        },
      })
    : icon;

  return (
    <ButtonBase
      component="div"
      onClick={onClick}
      style={{ width: "100%" }}
      sx={{
        transition: "background-color 0.2s ease, color 0.2s ease",
        "&:hover": {
          backgroundColor: `${colors.redAccent[500]}1a`,
        },
        "&:hover .settings-menu-icon svg": {
          color: `${colors.redAccent[500]} !important`,
        },
        "&:hover .settings-menu-label .MuiTypography-root": {
          color: `${colors.redAccent[500]} !important`,
        },
        textAlign: "center",
        alignContent: "center",
        alignItems: "center",
      }}
    >
      <ListItem>
        <ListItemIcon className="settings-menu-icon">{coloredIcon}</ListItemIcon>
        <ListItemText
          primary={label}
          className="settings-menu-label"
          primaryTypographyProps={{
            sx: { color: isActive ? colors.redAccent[500] : colors.grey[100] },
          }}
        />
      </ListItem>
    </ButtonBase>
  );
};

export default SettingsMenu;
